import { useEffect, useState } from "react";
import { fetchImages } from "../../hooks/useFetchImages";
import { Badge, Button, Card, Form, Modal, OverlayTrigger, Spinner, Tooltip } from "react-bootstrap";
import { VectorizedStatus } from "../../types/vectorization";
import { useLoadOptions } from "../../hooks/useLoadOptions";
import { useTranslation } from "react-i18next";
import { IImageGenerationDataModel } from "../../types/imageGeneration";
import pendingVectorization from "../../assets/vectorization_pending.gif";
import failedVectorization from "../../assets/vectorization_failed.png";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload, faStar } from "@fortawesome/free-solid-svg-icons";
import { submitImageGenerationFeedback } from "../../services/imageGeneration/submitImageGenerationFeedback";
import { toast } from "react-toastify";

interface IModelResultProps {
    generatedImage: IImageGenerationDataModel;
}

const ModelResult: React.FC<IModelResultProps> = ({ generatedImage }) => {
    const { t } = useTranslation();
    const { statusOptions } = useLoadOptions();
    const [selectedImageDisplay, setSelectedImageDisplay] = useState<boolean>(false);
    const [isDisplayFeedbackModal, setIsDisplayFeedbackModal] = useState<boolean>(false);
    const [selectedRating, setSelectedRating] = useState<number>(0);
    const [feedbackText, setFeedbackText] = useState<string>("");
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [img, setImg] = useState<string | undefined>(undefined);

    useEffect(() => {
        const fetchAndSetImage = async () => {
            switch (generatedImage.status) {
                case VectorizedStatus.COMPLETED:
                    setImg(await fetchImages(generatedImage.output_image));
                    break;
                case VectorizedStatus.PENDING:
                    setImg(pendingVectorization);
                    break;
                case VectorizedStatus.FAILED:
                    setImg(failedVectorization);
                    break;
            }
        };
        fetchAndSetImage();
    }, [generatedImage.output_image, generatedImage.status]);

    // Function to download the image
    const downloadImage = (imgUrl?: string) => {
        if (!imgUrl) return;
        const link = document.createElement("a");
        link.href = imgUrl;
        link.download = generatedImage.output_image.split("/").pop() || "image.png";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const openFeedbackModal = () => {
        setSelectedRating(generatedImage.feedback_rating || 0);
        setFeedbackText(generatedImage.feedback_text || "");
        setIsDisplayFeedbackModal(true);
    };

    const submitFeedback = async () => {
        const parsed_result = await submitImageGenerationFeedback(generatedImage.pk, generatedImage.sk, selectedRating, feedbackText);
        if (parsed_result.status === "Error") {
            toast.error(t(parsed_result.message || "ImageGeneration.FeedbackError"));
        }
        else if (parsed_result.status === "Success") {
            toast.success(t("ImageGeneration.FeedbackSuccess"));
        }
        setIsLoading(false);
        setIsDisplayFeedbackModal(false);
    };

    return (
        <div className="position-relative pt-3">
            <div className="position-absolute z-1 vector-info-badge">
                <Badge bg={statusOptions[generatedImage.status]?.color}>
                    {t(statusOptions[generatedImage.status]?.label)}
                </Badge>
            </div>
            <Card className="card-wrap-image">
                <Card.Body className="d-flex flex-column justify-content-center align-items-center gap-2">
                    <Card.Img
                        className={generatedImage.status === VectorizedStatus.COMPLETED ? "pointer-hover" : ""}
                        variant="top"
                        src={img}
                        alt={generatedImage.output_image}
                        onClick={() => setSelectedImageDisplay(true)}
                    />
                    <>
                        <OverlayTrigger
                            trigger={["hover", "focus"]}
                            placement="right"
                            overlay={<Tooltip>{generatedImage.model_name}</Tooltip>}>
                            <Badge bg="secondary">
                                {generatedImage.model_name}
                                <br />
                            </Badge>
                        </OverlayTrigger>
                        {generatedImage.feedback_rating !== null &&
                            <div className="d-flex justify-content-center gap-1">
                                {[1, 2, 3, 4, 5].map((star) => (
                                    <FontAwesomeIcon
                                        key={star}
                                        icon={faStar}
                                        className={`${(generatedImage.feedback_rating && generatedImage.feedback_rating >= star) ? "text-warning" : "text-muted"}`}
                                        aria-label={`Rate ${star} stars`}
                                    />
                                ))}
                            </div>
                        }
                    </>

                </Card.Body>

                <Card.Footer className="d-flex flex-row gap-2 justify-content-center">
                    <Button
                        className="d-flex flex-row gap-1 align-items-baseline"
                        variant="secondary"
                        disabled={generatedImage.status !== VectorizedStatus.COMPLETED}
                        onClick={() => downloadImage(img)}
                    >
                        {t("Vectorization.Download")}
                        <FontAwesomeIcon icon={faDownload} />
                    </Button>
                    <Button
                        variant="secondary"
                        disabled={generatedImage.status !== VectorizedStatus.COMPLETED}
                        onClick={() => openFeedbackModal()}
                    >
                        {t("Vectorization.Rating")}
                    </Button>

                </Card.Footer>


            </Card>
            <Modal show={isDisplayFeedbackModal} onHide={() => setIsDisplayFeedbackModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Feedback</Modal.Title>
                </Modal.Header>
                <Modal.Body className="d-flex flex-column gap-5">
                    <div className="d-flex justify-content-center gap-2">
                        {[1, 2, 3, 4, 5].map((star) => (
                            <FontAwesomeIcon
                                key={star}
                                icon={faStar}
                                size="2x"
                                className={`cursor-pointer ${selectedRating >= star ? "text-warning" : "text-muted"}`}
                                onClick={() => setSelectedRating(star)}
                            />
                        ))}
                    </div>
                    <Form.Control
                        as="textarea"
                        rows={4}
                        value={feedbackText}
                        onChange={(e) => setFeedbackText(e.target.value)}
                        placeholder={t("Vectorization.FeedbackTextPlaceHolder")}
                    />
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="primary" onClick={submitFeedback} disabled={selectedRating === 0}>
                        {isLoading ?
                            (
                                <>
                                    <Spinner as="span" animation="border" size="sm" role="status"
                                        aria-hidden="true" />
                                    {' '}{t("DropZone.loading")}
                                </>
                            ) : t("Vectorization.SendFeedback")
                        }
                    </Button>
                </Modal.Footer>
            </Modal>

            {<Modal show={selectedImageDisplay === true} onHide={() => setSelectedImageDisplay(false)} centered size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>{t("Image Preview")}</Modal.Title>
                </Modal.Header>
                <Modal.Body className="text-center">
                    <img src={img} className="img-fluid" />
                </Modal.Body>
            </Modal>}
        </div>
    )
}

export default ModelResult;
